// Phase 10A.1 R0/R1 — Watchlist page mock-data purge.
//
// Removed in this slice:
//   - MOCK_WATCHLIST (8 fictional fixtures: Hearts vs Kotoko,
//     Real Madrid vs Atletico, Bayern vs Leverkusen, Inter vs Napoli,
//     Arsenal vs Man City, Chelsea vs Tottenham, PSG vs Marseille,
//     Liverpool vs Brighton — all with invented edges + alerts).
//   - MOCK_SUGGESTIONS (3 fabricated "Sharp edge" recommendations).
//   - The `mappedItems.length > 0 ? mappedItems : MOCK_WATCHLIST`
//     fallback — when no real watchlist items exist, the page now
//     shows a truthful empty state instead of fake fixtures.

import { useState } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import { useSignalWatchlist } from "../../exchange/useSignalWatchlist.ts";
import { useWatchlistItems } from "../../watchlist/useWatchlistItems.ts";
import { AwaitingDataPanel } from "../_awaiting-data.tsx";

export function WatchlistPage(): JSX.Element {
  const { items, loading, error, refetch } = useWatchlistItems();
  const { toggleWatch, togglingFixtureId } = useSignalWatchlist();
  const [removeError, setRemoveError] = useState<string | null>(null);

  async function onRemove(fixtureId: string): Promise<void> {
    setRemoveError(null);
    const result = await toggleWatch(fixtureId);
    if (!result.ok) {
      setRemoveError("Could not remove. Try again.");
      return;
    }
    await refetch();
  }

  return (
    <>
      <div className="ex-head">
        <div>
          <h1>Watchlist</h1>
          <div className="sub">
            Markets you&rsquo;re tracking. Items you mark from /exchange surface here as read-only
            watchlist entries.
          </div>
        </div>
        <div className="pill-row">
          <span className="tag">Read-only</span>
        </div>
      </div>

      <div className="disclosure">
        <Icon name="info" size={14} />
        <span>
          STRIKZA does not auto-bet, alert your platform, or execute on watched markets. The
          watchlist is a private bookmark list scoped to your account.
        </span>
      </div>

      {loading ? (
        <AwaitingDataPanel
          eyebrow="Watchlist"
          title="Loading…"
          body="Authenticated read pending."
        />
      ) : error !== null ? (
        <AwaitingDataPanel eyebrow="Watchlist" title="Watchlist unavailable." body={error} />
      ) : items.length === 0 ? (
        <AwaitingDataPanel
          eyebrow="Watchlist"
          title="Nothing on your watchlist yet."
          body={
            <>
              Mark public-ready signal cards from <strong>/exchange</strong> to add them here.
              STRIKZA never seeds watchlist entries on your behalf.
            </>
          }
        />
      ) : (
        <div
          style={{
            marginTop: 24,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {items.map((item) => (
            <article
              key={item.id}
              style={{
                padding: 16,
                border: "1px solid var(--border-faint, #2a2a2a)",
                borderRadius: 8,
                background: "var(--bg-elev-1, transparent)",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <header
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-head, inherit)",
                    fontSize: 15,
                    color: "var(--text-primary, inherit)",
                    lineHeight: 1.3,
                  }}
                >
                  {item.homeTeam} vs {item.awayTeam}
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-mono, monospace)",
                    fontSize: 11,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    color: "var(--text-tertiary, #666)",
                  }}
                >
                  {item.league !== "" ? item.league : "league pending"}
                </span>
              </header>

              {item.signal !== undefined ? (
                <div
                  style={{
                    fontFamily: "var(--font-mono, monospace)",
                    fontSize: 12,
                    color: "var(--text-secondary, #888)",
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 6,
                  }}
                >
                  <span style={{ color: "var(--text-primary, inherit)" }}>
                    {item.signal.market}
                    {item.signal.selection !== "" ? ` · ${item.signal.selection}` : ""}
                  </span>
                  <span>
                    {item.signal.odds !== null ? item.signal.odds.toFixed(2) : "odds pending"}
                  </span>
                  {item.signal.headline !== "" ? (
                    <span style={{ gridColumn: "1 / -1" }}>{item.signal.headline}</span>
                  ) : null}
                </div>
              ) : (
                <span
                  style={{
                    fontFamily: "var(--font-mono, monospace)",
                    fontSize: 12,
                    color: "var(--text-tertiary, #666)",
                  }}
                >
                  No public signal card attached yet.
                </span>
              )}

              <footer
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-mono, monospace)",
                    fontSize: 11,
                    color: "var(--text-tertiary, #666)",
                  }}
                >
                  Read-only · added {item.addedAt.replace("T", " ").replace(/\..*$/, "Z")}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    void onRemove(item.fixtureId);
                  }}
                  disabled={togglingFixtureId === item.fixtureId}
                  style={{
                    border: "1px solid var(--border-faint, #2a2a2a)",
                    background: "transparent",
                    color: "var(--text-secondary, inherit)",
                    borderRadius: 4,
                    padding: "4px 10px",
                    cursor: togglingFixtureId === item.fixtureId ? "wait" : "pointer",
                    fontFamily: "var(--font-mono, monospace)",
                    fontSize: 11,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    opacity: togglingFixtureId === item.fixtureId ? 0.5 : 1,
                  }}
                  aria-label={`Remove ${item.homeTeam} vs ${item.awayTeam} from watchlist`}
                >
                  {togglingFixtureId === item.fixtureId ? "Removing…" : "Unwatch"}
                </button>
              </footer>
            </article>
          ))}
          {removeError !== null ? (
            <span
              role="alert"
              style={{
                fontFamily: "var(--font-mono, monospace)",
                fontSize: 12,
                color: "var(--text-secondary, #888)",
              }}
            >
              {removeError}
            </span>
          ) : null}
        </div>
      )}
    </>
  );
}
